/** Audio manifest (generated by tools/audio/synth.mjs into public/assets/audio/manifest.json). */
export type AudioKind = 'music' | 'ambience' | 'sfx';
export interface AudioEntry {
  kind: AudioKind;
  /** Paths relative to `<base>assets/` in preference order (ogg/opus first, then m4a). */
  files: string[];
  loop: boolean;
  /** Authoring gain applied on top of the bus volume. */
  gain: number;
  durationSec: number;
  loopSec?: number;
  bpm?: number;
  title?: string;
  lufs?: number;
  peakDb?: number;
}
export interface AudioManifest { version: number; sampleRate: number; entries: Record<string, AudioEntry> }

export interface AudioCaps { ogg: boolean; opus: boolean; m4a: boolean; mp3: boolean; webAudio: boolean }

/** Pick the first file the device can play. `.ogg` files are Opus-in-Ogg (needs ogg or opus support). */
export function pickFile(files: readonly string[], caps: AudioCaps): string | null {
  for (const f of files) {
    const ext = f.slice(f.lastIndexOf('.') + 1).toLowerCase();
    if (ext === 'ogg' && (caps.ogg || caps.opus)) return f;
    if (ext === 'opus' && caps.opus) return f;
    if ((ext === 'm4a' || ext === 'aac' || ext === 'mp4') && caps.m4a) return f;
    if (ext === 'mp3' && caps.mp3) return f;
    if (ext === 'wav') return f;
  }
  return null;
}

/** Fetch + validate the manifest. Never throws; returns null on any failure. */
export async function fetchAudioManifest(url: string): Promise<AudioManifest | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as Partial<AudioManifest>;
    if (!json || typeof json !== 'object' || !json.entries) return null;
    return { version: json.version ?? 1, sampleRate: json.sampleRate ?? 44100, entries: json.entries };
  } catch {
    return null;
  }
}

/** Well-known keys used by content (kept in one place so typos are caught by the showcase). */
export const AudioKeys = {
  music: { letter: 'music-letter', harbor: 'music-harbor', innRain: 'music-inn-rain', sparring: 'music-sparring', omen: 'music-omen' },
  ambience: { waterLap: 'amb-water-lap', rainLight: 'amb-rain-light', rainHeavy: 'amb-rain-heavy', rainShell: 'amb-rain-shell', wind: 'amb-wind', leaves: 'amb-leaves', gulls: 'amb-gulls', marketCrowd: 'amb-market-crowd', tavernChatter: 'amb-tavern-chatter', ritualHum: 'amb-ritual-hum', heartbeat: 'amb-heartbeat', omenDrone: 'amb-omen-drone' },
  sfx: {
    footstepSand: 'sfx-footstep-sand', footstepWood: 'sfx-footstep-wood', footstepStone: 'sfx-footstep-stone', boatCreak: 'sfx-boat-creak', thunder: 'sfx-thunder',
    cups: 'sfx-cups', laughter: 'sfx-laughter-burst', glyphGlow: 'sfx-glyph-glow', ritualTriangle: 'sfx-ritual-triangle', blackWater: 'sfx-black-water', doorClose: 'sfx-door-close',
    tableSlam: 'sfx-table-slam', stoolScrape: 'sfx-stool-scrape', shout: 'sfx-shout', hush: 'sfx-hush', sealWax: 'sfx-seal-wax', bottleCork: 'sfx-bottle-cork', heartbeat: 'sfx-heartbeat', stumble: 'sfx-stumble',
    uiMove: 'ui-move', uiConfirm: 'ui-confirm', uiCancel: 'ui-cancel', uiOpen: 'ui-open',
    swordSwing: 'sfx-sword-swing', swordHit: 'sfx-sword-hit', swordParry: 'sfx-sword-parry', swordGuard: 'sfx-sword-guard', hitSpark: 'sfx-hit-spark',
  },
} as const;

/**
 * Content ids → manifest keys. Chapter/UI builders reference cues by intent ('bgm-memory-storm',
 * 'cup-set', ...); the director resolves them here so scripts never carry file names. Unknown keys
 * pass through unchanged (and warn once when they are not in the manifest either).
 */
export const AUDIO_ALIASES: Record<string, string> = {
  // music (5 cues; memory stages share the letter theme — they are the letter, told in its voice)
  'bgm-prologue-letter': 'music-letter', 'bgm-memory-storm': 'music-letter', 'bgm-memory-island': 'music-letter', 'bgm-memory-farewell': 'music-letter',
  'bgm-memory-sickroom': 'music-omen', 'bgm-memory-road': 'music-sparring', 'bgm-prologue-prophecy': 'music-omen',
  'gh-market-day': 'music-harbor', 'dev-bgm-harbor': 'music-harbor', 'inn-rain': 'music-inn-rain', 'bgm-omen': 'music-omen',
  // sfx
  'sfx-ritual-glow': 'sfx-glyph-glow', 'cup-set': 'sfx-cups', 'crowd-laugh': 'sfx-laughter-burst', 'table-slam': 'sfx-table-slam', 'stool-scrape': 'sfx-stool-scrape',
  shout: 'sfx-shout', 'ui-hush': 'sfx-hush', heartbeat: 'sfx-heartbeat', 'door-close': 'sfx-door-close', 'seal-wax': 'sfx-seal-wax', 'bottle-cork': 'sfx-bottle-cork',
  'hit-spark': 'sfx-hit-spark', stumble: 'sfx-stumble', 'sword-swing': 'sfx-sword-swing', 'sword-hit': 'sfx-sword-hit', 'sword-parry': 'sfx-sword-parry', 'sword-guard': 'sfx-sword-guard', thunder: 'sfx-thunder',
};

/** Ambience ids → layer mixes (one content id may open several beds). */
export const AMBIENCE_ALIASES: Record<string, { key: string; volume: number }[]> = {
  'amb-harbor-water': [{ key: 'amb-water-lap', volume: 0.8 }], 'amb-harbor-gulls': [{ key: 'amb-gulls', volume: 0.6 }],
  'gh-harbor-water': [{ key: 'amb-water-lap', volume: 0.8 }], 'gh-market-crowd': [{ key: 'amb-market-crowd', volume: 0.7 }], 'dev-amb-sea': [{ key: 'amb-water-lap', volume: 0.8 }],
  'amb-storm-rain': [{ key: 'amb-rain-heavy', volume: 1 }], 'amb-storm-sea': [{ key: 'amb-water-lap', volume: 0.7 }, { key: 'amb-wind', volume: 1 }],
  'amb-island-waves': [{ key: 'amb-water-lap', volume: 0.7 }], 'amb-island-gulls': [{ key: 'amb-gulls', volume: 0.7 }],
  'amb-winter-wind': [{ key: 'amb-wind', volume: 0.55 }], 'amb-road-wind': [{ key: 'amb-wind', volume: 0.4 }, { key: 'amb-leaves', volume: 0.5 }],
  'rain-on-shell': [{ key: 'amb-rain-shell', volume: 0.8 }], 'inn-crowd': [{ key: 'amb-tavern-chatter', volume: 0.9 }],
};

/** Canonical manifest key for a content id. */
export function resolveAudioKey(key: string): string { return AUDIO_ALIASES[key] ?? key; }

/** Footstep SFX key for a surface type ('sand', 'wood', 'stone'). */
export function footstepKeyFor(surface?: 'sand' | 'wood' | 'stone' | string): string {
  switch (surface) {
    case 'wood': return 'sfx-footstep-wood';
    case 'stone': return 'sfx-footstep-stone';
    case 'sand':
    default:
      return 'sfx-footstep-sand';
  }
}

/** Expand content ambience ids into manifest layers; duplicates keep the loudest volume. */
export function expandAmbience(layers: readonly { key: string; volume: number }[]): { key: string; volume: number }[] {
  const out = new Map<string, number>();
  for (const l of layers) {
    const mix = AMBIENCE_ALIASES[l.key] ?? [{ key: resolveAudioKey(l.key), volume: 1 }];
    for (const m of mix) {
      const v = Math.max(0, Math.min(1, m.volume * l.volume));
      if (v > 0) out.set(m.key, Math.max(out.get(m.key) ?? 0, v));
    }
  }
  return [...out].map(([key, volume]) => ({ key, volume }));
}

/** Ambience presets per world state (data-driven; content may pass its own layers). */
export const AmbiencePresets: Record<string, { key: string; volume: number }[]> = {
  'harbor-sultry': [{ key: 'amb-water-lap', volume: 0.8 }, { key: 'amb-market-crowd', volume: 0.7 }, { key: 'amb-leaves', volume: 0.25 }],
  'harbor-wind': [{ key: 'amb-water-lap', volume: 0.7 }, { key: 'amb-market-crowd', volume: 0.5 }, { key: 'amb-wind', volume: 0.8 }, { key: 'amb-leaves', volume: 0.7 }],
  'harbor-drizzle': [{ key: 'amb-water-lap', volume: 0.6 }, { key: 'amb-market-crowd', volume: 0.35 }, { key: 'amb-rain-light', volume: 0.8 }, { key: 'amb-wind', volume: 0.4 }],
  'harbor-rain': [{ key: 'amb-water-lap', volume: 0.5 }, { key: 'amb-rain-heavy', volume: 0.9 }, { key: 'amb-wind', volume: 0.5 }],
  'inn-interior-rain': [{ key: 'amb-rain-light', volume: 0.5 }, { key: 'amb-tavern-chatter', volume: 0.9 }],
  'memory-storm': [{ key: 'amb-rain-heavy', volume: 1 }, { key: 'amb-wind', volume: 1 }, { key: 'amb-water-lap', volume: 0.6 }],
  'memory-island': [{ key: 'amb-water-lap', volume: 0.7 }, { key: 'amb-wind', volume: 0.3 }],
  'memory-sickroom': [{ key: 'amb-wind', volume: 0.25 }, { key: 'amb-omen-drone', volume: 0.5 }],
  'omen-end': [{ key: 'amb-heartbeat', volume: 0.8 }, { key: 'amb-omen-drone', volume: 0.7 }, { key: 'amb-rain-light', volume: 0.2 }],
  silence: [],
};
